/**
 * Module for CRT monitor style cursor. Extrapolated from [INSERT]'s code
 */

import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import bwNoise from '../assets/cursor/bwNoise.gif';
import colouredNoise from '../assets/cursor/coloredNoise.gif'

export default function Cursor() {
  const crtRef = useRef(null);
  const crtRef2 = useRef(null);
  const crtRef3 = useRef(null);
  const crtRef4 = useRef(null);
  const crtAntena = useRef(null);
  const crtAntena2 = useRef(null);
  const crtAntenaCopy = useRef(null);
  const crtAntena2Copy = useRef(null);
  const crtDial = useRef(null);
  const crtDial2 = useRef(null);
  const overlayRef = useRef(null);
  const leg = useRef(null);
  const leg2 = useRef(null);
  const grid = useRef(null);
  const circularCover = useRef(null);
  const cursor = useRef(null);

  const [isHovered, setIsHovered] = useState(false);

  function setAllTransformations(element, transformation) {
    element.style.transform = transformation;
    element.style.webkitTransform = transformation;
    element.style.mozTransform = transformation;
    element.style.msTransform = transformation;
    element.style.oTransform = transformation;
  }

  const location = useLocation();

  // For hover cursor background changes, uses event delegation
  useEffect(() => {
    const handleMouseEnter = (e) => {
      // List of all elements that should show as hoverable
      if (e.target.closest(`
        button, 
        a,
        input,
        .hover-trigger, 
        .primary-btn, 
        .secondary-btn, 
        .hamburgermenu, 
        .product-image, 
        .see-more-label, 
        .x,
        .work-type,
        .details
      `)) {
        setIsHovered(true);
      }
    };

    const handleMouseLeave = () => setIsHovered(false);

    document.addEventListener("mouseover", handleMouseEnter);
    document.addEventListener("mouseout", handleMouseLeave);

    return () => {
      document.removeEventListener("mouseover", handleMouseEnter);
      document.removeEventListener("mouseout", handleMouseLeave);
    };
  }, [location.pathname]);
  
  // For cursor transformations
  useEffect(() => {
    // TRANSFORMATIONS
    const crtElement = crtRef.current;
    const crtElement2 = crtRef2.current;
    const crtElement3 = crtRef3.current;
    const crtElement4 = crtRef4.current;
    const crtAntenaElement = crtAntena.current;
    const crtAntenaElement2 = crtAntena2.current;
    const crtAntenaElementCopy = crtAntenaCopy.current;
    const crtAntenaElement2Copy = crtAntena2Copy.current;
    const crtDialElement = crtDial.current;
    const crtDialElement2 = crtDial2.current;
    const overlayElement = overlayRef.current;
    const legElement = leg.current;
    const legElement2 = leg2.current;
    const gridElement = grid.current;
    const circularCoverElement = circularCover.current;
    const cursorElement = cursor.current;

    // Check if elements have been mounted to the DOM
    if (!crtElement || !overlayElement || !crtElement2 || !crtElement3 || !crtElement4 || !crtAntenaElement || 
      !crtAntenaElement2 || !crtDial || !crtDial2 || !legElement || !legElement2 || !gridElement || !circularCoverElement || 
      !crtAntenaElementCopy || !crtAntenaElement2Copy || !cursorElement) {
      return;
    }

    const zoomLevel = 0.7;
    let currentScale = 0;

    const mouse = {
      x: window.innerWidth / 2 / zoomLevel,
      y: window.innerHeight / 2 / zoomLevel
    };
    const prevMouse = { ...mouse };
    const crt = { ...mouse };
    const crt2 = { ...mouse };
    const crt3 = { ...mouse };
    const crt4 = { ...mouse };
    const prevCrt = { ...mouse };
    const antenaRotation = { left: 0, right: 0 };
    const antenaRotationCopy = { left: 0, right: 0 };

    let hasMoved = false;

    const handleMouseMove = (e) => {
      hasMoved = true;
      mouse.x = e.clientX / zoomLevel;
      mouse.y = e.clientY / zoomLevel;
    };

    window.addEventListener("mousemove", handleMouseMove);

    // VAR DECLARATION
    // Transforms
    let translateTransform;
    let translateTransform2;
    let translateTransform3;
    let translateTransform4;
    let rotationTransform;
    let scaleTransform;
    let translateTransformAntena;
    let translateTransformAntena2;
    let rotationTransformAntena;
    let rotationTransformAntena2;
    let rotationTransformAntenaCopy;
    let rotationTransformAntena2Copy;

    // Incrementing values for lerp
    let perspectiveAngle = 0;
    let scaleValue = 0;
    let translateValueAntena = 0;
    let rotateAngleAntena = 0;
    let rotateAngleAntena2 = 0;
    let deltaCrtX;  // Change in crt x pos 
    let deltaCrtY;  // Change in crt y pos
    let mouseVelocity;
    let normalizedVelocity;
    
    const speed = 0.20;
    const slowerSpeed = 0.19;
    const slowerSpeed2 = 0.18;
    const slowerSpeed3 = 0.17;
    const slowerSpeed4 = 0.16;
    const maxAntenaSpeed = 7;
    const maxPerspectiveAngle = 5;
    const maxAntenaAngle = 0.2;
    const maxScaleValue = 0.4;

    let st;
    let prevScrollTop = window.pageYOffset || document.documentElement.scrollTop;
    let prevScrollDirection = '';

    /**
     * Animation loop. Transforms CRT cursor based on user movement.
     */
    const tick = () => {
      if (!hasMoved) {
        requestAnimationFrame(tick);
        return;
      }

      st = window.pageYOffset || document.documentElement.scrollTop;
      if (st == prevScrollTop && prevScrollDirection !== 'still') {
        prevScrollDirection = 'still';
      } else if (st > prevScrollTop && prevScrollDirection !== 'down') {
        // downscroll code here
        prevScrollDirection = 'down';
      } else if (st < prevScrollTop && prevScrollDirection !== 'up') {
        // upscroll code
        prevScrollDirection = 'up';
      }
      prevScrollTop = st <= 0 ? 0 : st; // for Mobile or negative scrolling

      // MOVE
      // Calculate where crt goes based on mouse position and smoothing (with lerp)
      crt.x += (mouse.x - crt.x) * speed;
      crt.y += (mouse.y - crt.y) * speed;
      translateTransform = `translate(${crt.x}px, ${crt.y}px)`;
      
      // Change in crt pos
      deltaCrtX = crt.x - prevCrt.x;
      deltaCrtY = crt.y - prevCrt.y;

      mouseVelocity = Math.min(Math.sqrt(deltaCrtX**2 + deltaCrtY**2) * 4, 100);  // Mouse velocity (Euclidean distance), max 150
      normalizedVelocity = (mouseVelocity / 100); // Normalized mouseVelocity

      // "Shadowing" crts
      crt2.x += (mouse.x - crt2.x) * slowerSpeed;
      crt2.y += (mouse.y - crt2.y) * slowerSpeed;
      translateTransform2 = `translate(${crt2.x}px, ${crt2.y}px)`;

      crt3.x += (mouse.x - crt3.x) * slowerSpeed2;
      crt3.y += (mouse.y - crt3.y) * slowerSpeed2;
      translateTransform3 = `translate(${crt3.x}px, ${crt3.y}px)`;

      crt4.x += (mouse.x - crt4.x) * slowerSpeed3;
      crt4.y += (mouse.y - crt4.y) * slowerSpeed3;
      translateTransform4 = `translate(${crt4.x}px, ${crt4.y}px)`;

      // Antena
      translateValueAntena = normalizedVelocity * maxAntenaSpeed;

      translateTransformAntena = `translate(${crt.x + translateValueAntena}px, ${crt.y + translateValueAntena}px)`;
      translateTransformAntena2 = `translate(${crt.x + translateValueAntena}px, ${crt.y + translateValueAntena}px)`;

      // SKEW (calculate rotation, combined with hardcoded perspective transformation later)
      // Range of rotation values for perspective skew is [0, 6]
      // Range of rotation values for antena is [0, 2]
      perspectiveAngle = normalizedVelocity * maxPerspectiveAngle;
      rotateAngleAntena = normalizedVelocity * maxAntenaAngle;
      rotateAngleAntena2 = normalizedVelocity * maxAntenaAngle;

      // Perspective skew for left and right mouse movement
      if (prevMouse.x == mouse.x) {
        // Mouse didn't move, don't show perspective
        rotationTransform = `rotateY(0deg)`
      } else if (prevMouse.x > mouse.x) {
        // Moving right, skew right side
        rotationTransform = `rotateY(${-perspectiveAngle}deg)`
        rotateAngleAntena = 20;
        rotateAngleAntena2 = 20;
      } else {
        // Moving left, skew left side
        rotationTransform = `rotateY(${perspectiveAngle}deg)`
        rotateAngleAntena = -20;
        rotateAngleAntena2 = -20;
      }

      // Perspective skew for up and down mouse movement
      if (prevMouse.y == mouse.y && prevScrollDirection === 'still') {
        // Mouse didn't move, don't show perspective
        rotationTransform += ` rotateY(0deg)`
      } else if (prevMouse.y < mouse.y || prevScrollDirection === 'down') {
        // Moving down, skew bottom side
        rotationTransform += ` rotateX(${-perspectiveAngle}deg)`
        rotateAngleAntena += -20;
        rotateAngleAntena2 += 20;
      } else {
        // Moving up, skew top side
        rotationTransform += ` rotateX(${perspectiveAngle}deg)`
        rotateAngleAntena += 20;
        rotateAngleAntena2 += -20;
      }
      
      antenaRotation.left += (rotateAngleAntena - antenaRotation.left) * slowerSpeed3;
      antenaRotation.right += (rotateAngleAntena2 - antenaRotation.right) * slowerSpeed3;
      antenaRotationCopy.left += (rotateAngleAntena - antenaRotation.left) * slowerSpeed4;
      antenaRotationCopy.right += (rotateAngleAntena2 - antenaRotation.right) * slowerSpeed4;

      // Antena
      rotationTransformAntena = `rotateZ(${20 + antenaRotation.left}deg)`
      rotationTransformAntena2 = `rotateZ(${-20 + antenaRotation.right}deg)`
      rotationTransformAntenaCopy = `rotateZ(${20 + antenaRotationCopy.left}deg)`
      rotationTransformAntena2Copy = `rotateZ(${-20 + antenaRotationCopy.right}deg)`

      // Update previous mouse position for the next frame
      prevCrt.x = crt.x;
      prevCrt.y = crt.y;
      prevMouse.x = mouse.x;
      prevMouse.y = mouse.y;

      // SQUISH AND SQUASH
      // Range of scale values is [0, 0.5]
      scaleValue = normalizedVelocity * maxScaleValue;
      // Lerp scale
      currentScale += (scaleValue - currentScale) * speed;
      scaleTransform = `scale(${1 + currentScale}, ${1 - currentScale})`;

      // Apply transformations in a specific order: translate -> rotate -> scale
      setAllTransformations(crtElement, `${translateTransform} ${scaleTransform} perspective(10px) ${rotationTransform} translateZ(0)`);
      setAllTransformations(crtElement2, `${translateTransform2} ${scaleTransform} perspective(10px) ${rotationTransform} translateZ(0)`);
      setAllTransformations(crtElement3, `${translateTransform3} ${scaleTransform} perspective(10px) ${rotationTransform} translateZ(0)`);
      setAllTransformations(crtElement4, `${translateTransform4} ${scaleTransform} perspective(10px) ${rotationTransform} translateZ(0)`);
      setAllTransformations(crtAntenaElement, `${translateTransformAntena} ${rotationTransformAntena} translateZ(0)`);
      setAllTransformations(crtAntenaElement2, `${translateTransformAntena2} ${rotationTransformAntena2} translateZ(0)`);
      setAllTransformations(crtAntenaElementCopy, `${translateTransformAntena} ${rotationTransformAntenaCopy} translateZ(0)`);
      setAllTransformations(crtAntenaElement2Copy, `${translateTransformAntena} ${rotationTransformAntena2Copy} translateZ(0)`);
      setAllTransformations(crtDialElement, `${translateTransform} ${scaleTransform} perspective(10px) ${rotationTransform} translateZ(0)`);
      setAllTransformations(crtDialElement2, `${translateTransform} ${scaleTransform} perspective(10px) ${rotationTransform} translateZ(0)`);
      setAllTransformations(legElement, `${translateTransform} ${scaleTransform} perspective(10px) ${rotationTransform} translateZ(0)`);
      setAllTransformations(legElement2, `${translateTransform} ${scaleTransform} perspective(10px) ${rotationTransform} translateZ(0)`);
      setAllTransformations(gridElement, `${translateTransform} ${scaleTransform} perspective(10px) ${rotationTransform} translateZ(0)`);
      setAllTransformations(circularCoverElement, `${translateTransform} ${scaleTransform} perspective(10px) ${rotationTransform} translateZ(0)`);
      setAllTransformations(overlayElement, `${translateTransform} ${scaleTransform} perspective(10px) ${rotationTransform} translateZ(0)`);

      // Request the next frame to continue the animation
      window.requestAnimationFrame(tick);
    };

    tick();
  }, []);

  return (
    <div ref={cursor} className="cursor">
      <div ref={crtRef} className="crt" id="crt1"></div>
      <div ref={crtRef2} className="crt" id="crt2"></div>
      <div ref={crtRef3} className="crt" id="crt3"></div>
      <div ref={crtRef4} className="crt" id="crt4"></div>
      <div ref={crtAntena} className="antena" id="antena1"></div>
      <div ref={crtAntena2} className="antena" id="antena2"></div>
      <div ref={crtAntenaCopy} className="antena" id="antena1Copy"></div>
      <div ref={crtAntena2Copy} className="antena" id="antena2Copy"></div>
      <div ref={crtDial} className="dial" id="dial1"></div>
      <div ref={crtDial2} className="dial" id="dial2"></div>
      <div ref={leg} className="leg" id="leg1"></div>
      <div ref={leg2} className="leg" id="leg2"></div>
      <div ref={grid} className="grid"></div>
      <div ref={circularCover} className="circularCover"></div>

      <div
        ref={overlayRef}
        className="overlay"
        style={{
          backgroundImage: isHovered
            ? `url(${colouredNoise})`
            : `url(${bwNoise})`
        }}
      />
    </div>
  );
}